import type { EventDto } from '~/lib/types';
import { ymLabel } from '~/lib/event-utils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DayInfo {
  count: number;
  hasSpotlight: boolean;
}

export function buildDayInfo(events: EventDto[]): Map<string, DayInfo> {
  const map = new Map<string, DayInfo>();
  for (const e of events) {
    const info = map.get(e.dayKey) ?? { count: 0, hasSpotlight: false };
    info.count += 1;
    if (e.spotlight) info.hasSpotlight = true;
    map.set(e.dayKey, info);
  }
  return map;
}

/**
 * Accessible month grid. Semantic table markup (thead of weekdays, buttons in
 * cells); each day shows event-count dots. Clicking a day selects it — the
 * caller renders that day's agenda below.
 */
export function MonthGrid({
  ym,
  dayInfo,
  todayKey,
  selectedDay,
  onSelectDay,
  onShiftMonth,
}: {
  ym: string; // 'YYYY-MM'
  dayInfo: Map<string, DayInfo>;
  todayKey: string;
  selectedDay: string | null;
  onSelectDay: (dayKey: string | null) => void;
  onShiftMonth: (delta: number) => void;
}) {
  const [y = 1970, mo = 1] = ym.split('-').map(Number);
  const firstDow = new Date(Date.UTC(y, mo - 1, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(y, mo, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');

  const cells: (number | null)[] = [
    ...Array.from({ length: firstDow }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  const monthShort = ymLabel(ym).split(' ')[0];

  return (
    <div>
      <div className="month-nav">
        <button
          type="button"
          className="ui-btn btn-secondary btn-sm"
          onClick={() => onShiftMonth(-1)}
          aria-label="Previous month"
        >
          ← Prev
        </button>
        <h2 aria-live="polite">{ymLabel(ym)}</h2>
        <button
          type="button"
          className="ui-btn btn-secondary btn-sm"
          onClick={() => onShiftMonth(1)}
          aria-label="Next month"
        >
          Next →
        </button>
      </div>

      <div className="month-grid" role="grid" aria-label={`${ymLabel(ym)} calendar`}>
        <div className="month-row" role="row">
          {WEEKDAYS.map((d) => (
            <div key={d} className="month-grid-head" role="columnheader" aria-label={d}>
              {d}
            </div>
          ))}
        </div>
        {weeks.map((week, w) => (
          <div className="month-row" role="row" key={`w-${w}`}>
            {week.map((day, i) => {
              if (day === null) {
                return <div key={`pad-${w}-${i}`} role="gridcell" aria-hidden="true" />;
              }
              const dayKey = `${y}-${pad(mo)}-${pad(day)}`;
              const info = dayInfo.get(dayKey);
              const classes = [
                'month-cell',
                dayKey === todayKey ? 'is-today' : '',
                dayKey === selectedDay ? 'is-selected' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const label = info
                ? `${monthShort} ${day}, ${info.count} event${info.count === 1 ? '' : 's'}`
                : `${monthShort} ${day}, no events`;
              return (
                <div key={dayKey} role="gridcell">
                  <button
                    type="button"
                    className={classes}
                    aria-label={label}
                    aria-pressed={dayKey === selectedDay ? 'true' : 'false'}
                    disabled={!info}
                    onClick={() => onSelectDay(dayKey === selectedDay ? null : dayKey)}
                  >
                    <span className="month-cell-num">{day}</span>
                    <span className="month-cell-dots" aria-hidden="true">
                      {info &&
                        Array.from({ length: Math.min(info.count, 3) }, (_, d) => (
                          <i key={d} className={d === 0 && info.hasSpotlight ? 'dot-spot' : ''} />
                        ))}
                    </span>
                    {info && info.count > 3 && (
                      <span className="month-cell-more" aria-hidden="true">
                        +{info.count - 3}
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
